import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { REJECTION_REASONS } from "../../../types/researchWorkflow";

interface RejectionReasonModalProps {
  open: boolean;
  entityName: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const RejectionReasonModal: React.FC<RejectionReasonModalProps> = ({
  open,
  entityName,
  onClose,
  onConfirm,
}) => {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [customReason, setCustomReason] = useState("");

  const handleToggleReason = (reasonId: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reasonId)
        ? prev.filter((id) => id !== reasonId)
        : [...prev, reasonId],
    );
  };

  const handleConfirm = () => {
    const parts = selectedReasons
      .map((id) => REJECTION_REASONS.find((r) => r.id === id)?.label)
      .filter(Boolean) as string[];
    if (customReason.trim()) {
      parts.push(customReason.trim());
    }
    onConfirm(parts.length > 0 ? parts.join(", ") : "No reason specified");
    setSelectedReasons([]);
    setCustomReason("");
  };

  const handleClose = () => {
    setSelectedReasons([]);
    setCustomReason("");
    onClose();
  };

  const canConfirm = selectedReasons.length > 0 || customReason.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#1e1e1e", backgroundImage: "none" } }}
    >
      <DialogTitle>Dismiss Entity</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Why are you dismissing <strong>{entityName}</strong>? Select all that
          apply:
        </Typography>
        <FormGroup>
          {REJECTION_REASONS.map((reason) => (
            <FormControlLabel
              key={reason.id}
              sx={{ mb: 1, alignItems: "flex-start" }}
              control={
                <Checkbox
                  checked={selectedReasons.includes(reason.id)}
                  onChange={() => handleToggleReason(reason.id)}
                  sx={{ pt: 0 }}
                />
              }
              label={
                <Typography variant="body2">{reason.label}</Typography>
              }
            />
          ))}
        </FormGroup>
        <TextField
          label="Other (custom reason)"
          value={customReason}
          onChange={(e) => setCustomReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          size="small"
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={!canConfirm}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejectionReasonModal;
